'use client';

import { useEffect, useState } from 'react';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:5000';

/**
 * Iframe renderer for bundle themes.
 *
 * Kept as a small client component so it can read `window.location.hostname`
 * (and localhost's `?school` dev override) to build the backend theme-render
 * URL at runtime. Used only by ServerSchoolLanding when the school's
 * active theme has no registered React component — i.e. when rendering
 * is entirely delegated to an uploaded static bundle.
 *
 * The URL is resolved inside `useEffect` (not `useMemo`) so the first client
 * render matches the SSR output of `null` — otherwise React tears down the
 * subtree with a hydration mismatch (server: null, client: <iframe>).
 */
export default function ClientIframeTheme({ tenant, initialPath }) {
    const [src, setSrc] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams({ hostname: window.location.hostname });

        // ALWAYS forward the resolved tenant's schoolCode when we have
        // one. This covers three cases:
        //   1. Real subdomain (cms.kaympus.com) — backend can resolve
        //      by hostname anyway, but the explicit code is a no-op
        //      safety net.
        //   2. Main-domain preview (kaympus.com?school=cms) — the
        //      backend can't resolve the tenant from kaympus.com, so
        //      the code is required.
        //   3. Local dev — previously gated by hostname; now flows
        //      through `tenant.schoolCode` just like prod.
        const resolvedCode = tenant?.schoolCode;
        if (resolvedCode) {
            params.set('school', resolvedCode);
        } else if (
            window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1'
        ) {
            // Fallback for local dev when no tenant prop is available
            // (standalone preview before the landing page wires it up).
            const saved =
                typeof localStorage !== 'undefined' ? localStorage.getItem('dev_school_code') : null;
            const urlSchool = new URLSearchParams(window.location.search).get('school');
            const dev = urlSchool || saved;
            if (dev) params.set('school', dev);
        }

        // Deep-link support: when the visitor opens a non-root URL on the
        // school's domain (e.g. /admission-process via Ctrl+click from the
        // theme's navbar), forward the path so the backend returns the
        // matching sub-route HTML with __LANDING_DATA__ already injected.
        const path = initialPath || window.location.pathname;
        if (path && path !== '/') params.set('path', path);

        setSrc(`${API_BASE_URL}/api/public/theme-render?${params}`);
    }, [initialPath, tenant?.schoolCode]);

    if (!src) return null;

    return (
        <iframe
            title={tenant?.schoolName || 'School Website'}
            src={src}
            style={{
                border: 'none',
                width: '100vw',
                height: '100vh',
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 1,
            }}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
        />
    );
}
